import { z } from 'zod';
import { FEATURE_KEYS, PLAN_KEYS, RESOURCES } from '../plans';
import { IsoDateTimeSchema, LocaleSchema, UuidSchema } from './common';
import { GoogleReviewUrlSchema } from './reviews';

export const TENANT_STATUSES = [
  'pending_subscription',
  'active',
  'past_due',
  'canceled',
  'suspended',
  /** Account closed: kept for accounting/audit retention, PII purged by a job (ADR-019). */
  'terminated',
] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

export const MEMBERSHIP_ROLES = ['owner', 'admin', 'member'] as const;
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number];

export const MEMBERSHIP_STATUSES = ['active', 'invited', 'disabled'] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

export const BUSINESS_CATEGORIES = [
  'barbershop',
  'hair_salon',
  'beauty_salon',
  'gym',
  'restaurant',
  'cafe',
  'retail',
  'auto_repair',
  'clinic',
  'other',
] as const;
export type BusinessCategory = (typeof BUSINESS_CATEGORIES)[number];

export const TenantSettingsSchema = z
  .object({
    defaultSenderName: z.string().max(11).optional(),
    replyToEmail: z.email().optional(),
    brandColor: z.string().optional(),
    logoFileId: UuidSchema.optional(),
  })
  .catchall(z.unknown());
export type TenantSettings = z.infer<typeof TenantSettingsSchema>;

/**
 * Versions of the legal documents a business accepts when it creates a tenant: the terms of
 * service and the data processing agreement (GrowHero processes the business's customer data on
 * its behalf). Bump a version when the text changes; POST /tenants refuses any other version.
 */
export const LEGAL_DOCUMENT_VERSIONS = {
  terms: '2026-10-draft-1',
  dpa: '2026-10-draft-1',
} as const;

export const LegalAcceptanceSchema = z.object({
  terms: z.literal(LEGAL_DOCUMENT_VERSIONS.terms),
  dpa: z.literal(LEGAL_DOCUMENT_VERSIONS.dpa),
});

export const CreateTenantRequestSchema = z.object({
  /** Required on every sign-up path: no tenant without accepted terms and DPA (current versions). */
  acceptLegal: LegalAcceptanceSchema,
  name: z.string().trim().min(2).max(120),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,46}[a-z0-9])?$/)
    .optional(),
  businessCategory: z.enum(BUSINESS_CATEGORIES).optional(),
  locale: LocaleSchema.default('el'),
  timezone: z.string().min(3).max(64).default('Europe/Athens'),
  country: z.string().length(2).toUpperCase().default('GR'),
  planKey: z.enum(PLAN_KEYS).default('basic'),
  location: z
    .object({
      name: z.string().trim().min(1).max(120).optional(),
      address: z.string().trim().max(300).optional(),
      // Same Google-host invariant as review settings / onboarding (https on a Google review host).
      googleReviewUrl: GoogleReviewUrlSchema.optional(),
    })
    .optional(),
});
export type CreateTenantRequest = z.input<typeof CreateTenantRequestSchema>;

export const TenantSchema = z.object({
  id: UuidSchema,
  slug: z.string(),
  name: z.string(),
  status: z.enum(TENANT_STATUSES),
  planKey: z.enum(PLAN_KEYS).nullable(),
  locale: LocaleSchema,
  timezone: z.string(),
  country: z.string(),
  businessCategory: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type Tenant = z.infer<typeof TenantSchema>;

export const MembershipSummarySchema = z.object({
  tenantId: UuidSchema,
  tenantName: z.string(),
  tenantSlug: z.string(),
  tenantStatus: z.enum(TENANT_STATUSES),
  planKey: z.enum(PLAN_KEYS).nullable(),
  role: z.enum(MEMBERSHIP_ROLES),
  status: z.enum(MEMBERSHIP_STATUSES),
});
export type MembershipSummary = z.infer<typeof MembershipSummarySchema>;

export const MeResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string().nullable(),
    displayName: z.string().nullable(),
    locale: LocaleSchema,
  }),
  memberships: z.array(MembershipSummarySchema),
  platformAdmin: z.boolean(),
  /**
   * Platform-switched features (PLATFORM_SWITCHED_FEATURES) turned on for this deployment. The web
   * app hides a switched feature entirely (no nav, route or upsell) unless it is listed here.
   */
  platformFeatures: z.array(z.enum(FEATURE_KEYS)).default([]),
  /**
   * How background work runs on this deployment (ADR-EX1). `scheduled`: a scheduler tick every
   * `tickMinutes` minutes in the daytime sends scheduled messages, so the UI tells owners that a
   * scheduled send goes out up to that many minutes after the chosen time.
   */
  execution: z
    .object({
      mode: z.enum(['continuous', 'scheduled']),
      tickMinutes: z.number().int().positive().nullable(),
    })
    .default({ mode: 'continuous', tickMinutes: null }),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const WalletSummarySchema = z.object({
  resource: z.enum(RESOURCES),
  available: z.number().int(),
  reserved: z.number().int(),
  consumed: z.number().int(),
  expired: z.number().int(),
  reversed: z.number().int(),
  totalGranted: z.number().int(),
});
export type WalletSummary = z.infer<typeof WalletSummarySchema>;

export const PlanSummarySchema = z.object({
  key: z.enum(PLAN_KEYS),
  name: z.string(),
  priceCents: z.number().int(),
  currency: z.string(),
  interval: z.string(),
  allowances: z.record(z.enum(RESOURCES), z.number().int()),
  limits: z.record(z.string(), z.unknown()),
});
export type PlanSummary = z.infer<typeof PlanSummarySchema>;

export const CurrentTenantResponseSchema = z.object({
  tenant: TenantSchema.extend({
    onboardingState: z.record(z.string(), z.unknown()),
    settings: z.record(z.string(), z.unknown()),
  }),
  role: z.enum(MEMBERSHIP_ROLES),
  plan: PlanSummarySchema.nullable(),
  features: z.array(z.enum(FEATURE_KEYS)),
  subscription: z
    .object({
      status: z.string(),
      currentPeriodEnd: IsoDateTimeSchema.nullable(),
      cancelAtPeriodEnd: z.boolean(),
    })
    .nullable(),
  /**
   * Free trial a platform admin granted (ADR-B11), until a paid subscription replaces it. `expired`
   * means the end date passed: a card is needed to continue.
   */
  trial: z.object({ endsAt: IsoDateTimeSchema, expired: z.boolean() }).nullable().default(null),
  wallets: z.array(WalletSummarySchema),
  defaultLocationId: UuidSchema.nullable(),
});
export type CurrentTenantResponse = z.infer<typeof CurrentTenantResponseSchema>;

export const CreateTenantResponseSchema = z.object({
  tenant: TenantSchema,
  membership: z.object({ role: z.enum(MEMBERSHIP_ROLES), status: z.enum(MEMBERSHIP_STATUSES) }),
  defaultLocationId: UuidSchema,
});
export type CreateTenantResponse = z.infer<typeof CreateTenantResponseSchema>;

export const InviteMemberRequestSchema = z.object({
  email: z.email(),
  role: z.enum(['admin', 'member']),
});
